import React, { useCallback, useMemo, useState } from 'react';
import { Cover, Grid, Header2Text, NavigationItem, SideSheet } from '../../../../ui-toolkit';
import { Form, Formik } from 'formik';
import { editInternalOrderContactsForm } from '../../../../validation/order/InternalOrderContactsForm';
import { SideSheetEditContactFormFooter } from '../../../shared/SideSheetEditContactFormFooter';
import { EditContactFormStatusWrapper } from '../subComponents/EditContactFormStatusWrapper';
import { OrderContactNotesField } from '../subComponents/OrderContactNotesField';
import { DeleteContactWarning } from '../subComponents/DeleteContactWarning';
import { IInternalContactData } from '../../../../types/contacts/IInternalContact';
import {
  useDeleteInternalOrderContactMutation,
  useUpdateInternalOrderContactMutation,
} from '../../../../API/order/contacts/InternalContactsApi';
import { convertToError } from '../../../../API/BaseApi';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { SelectInternalContactRoles } from './SelectInternalContactRoles';
import { useListRolesQuery } from '../../../../API/roles/RolesApi';
import {
  convertFromIInternalContactsDataToIInternalContactsRequest,
  IUpdateInternalContactRequest,
} from '../../../../types/contacts/IInternalContact';

export const EditInternalOrderContactForm: React.FC<{
  editContact: IInternalContactData;
  setEditContact: React.Dispatch<React.SetStateAction<IInternalContactData | null>>;
}> = ({ editContact, setEditContact }) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const { data: roles = [] } = useListRolesQuery();

  const validationSchema = useMemo(
    () => editInternalOrderContactsForm(roles.map((role) => role.name)),
    [roles],
  );

  const [
    deleteInternalOrderContact,
    {
      isLoading: deleteInternalOrderContactLoading,
      isSuccess: deleteInternalOrderContactSuccess,
      error: deleteInternalOrderContactError,
    },
  ] = useDeleteInternalOrderContactMutation();

  const [
    updateInternalOrderContact,
    {
      isLoading: updateInternalOrderContactLoading,
      isSuccess: updateInternalOrderContactSuccess,
      error: updateInternalOrderContactError,
    },
  ] = useUpdateInternalOrderContactMutation();

  const onDelete = useCallback(
    async (contactId: UUID) => {
      const result = await deleteInternalOrderContact({ orderId, contactId });
      checkForMutationSuccess(result, 'Delete Internal Contact');
    },
    [deleteInternalOrderContact, orderId]
  );

  const onSubmit = useCallback(
    async (contact: IUpdateInternalContactRequest) => {
      const result = await updateInternalOrderContact({ orderId, contact });
      checkForMutationSuccess(result, 'Update Internal Contact');
    },
    [orderId, updateInternalOrderContact]
  );

  const onCancel = useCallback(() => {
    setEditContact(null);
  }, [setEditContact]);

  const onDeleteCancelled = () => {
    setShowDeleteWarning(false);
    onCancel();
  };

  const showWarning = () => setShowDeleteWarning(true);

  // Convert editContact to form values format (roles as string[] instead of IRoleData[])
  const initialValues = useMemo(
    () => convertFromIInternalContactsDataToIInternalContactsRequest(editContact),
    [editContact],
  );

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      <Form>
        <SideSheet
          title={'Edit Contact'}
          onClose={onCancel}
          footer={
            <SideSheetEditContactFormFooter
              onCancel={onCancel}
              saveButtonText={'Save and Update'}
              showWarning={showWarning}
            />
          }
        >
          <Grid>
            <EditContactFormStatusWrapper
              fetching={updateInternalOrderContactLoading || deleteInternalOrderContactLoading}
              updateFetchedSuccess={updateInternalOrderContactSuccess}
              deleteFetchedSuccess={deleteInternalOrderContactSuccess}
              fetchError={convertToError(updateInternalOrderContactError ?? deleteInternalOrderContactError)}
              updateSuccessMessage={'Contact Updated!'}
              deleteSuccessMessage={'Contact Deleted!'}
              onResetDeleteAndCancel={onCancel}
            />
            <div>
              <Header2Text>
                {editContact.firstName} {editContact.lastName}
              </Header2Text>
              <NavigationItem
                href={`mailto:${editContact.emailAddress}`}
                text={editContact.emailAddress}
                isActive
                isInline
              />
            </div>
            <SelectInternalContactRoles />
            <OrderContactNotesField name={'notes'} />

            {showDeleteWarning && (
              <Cover>
                <DeleteContactWarning
                  contactId={editContact.id}
                  onCancel={onDeleteCancelled}
                  onDelete={onDelete}
                  contactName={`${editContact.firstName} ${editContact.lastName}`}
                />
              </Cover>
            )}
          </Grid>
        </SideSheet>
      </Form>
    </Formik>
  );
};
